import {
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack
} from "@mui/material";
import Button from "@mui/material/Button";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import useApiHook from "../hooks/useApiHook";
import { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import VisuallyHiddenInput from "./_atoms/inputs/VisuallyHiddenInput";
import UploadProgress from "./_atoms/UploadProgress";
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import { usePackageModal } from "../store/modal-context";
import { useUser } from "../store/user-context";
import HiddenContentSwitch from "./_atoms/inputs/HiddenContentSwitch";
import { getAudioDuration } from "../utils/audio";
import TranscriptionEstimateDialog from "./TranscriptionEstimateDialog";
import cancelButtonSx from "../styles/cancelButtonSx";

const FileUploadDialog = ({ currentPatientId = null, currentPatientName = null, onFileUpload, onClose, open, autoOpenFilePicker = false, preselectedFile = null, onRetryAfterError, onTranscriptionStarted }) => {
    const { response, makeRequest, loading, uploadProgress, cancelRequest, error: requestError } = useApiHook();
    const [fileName, setFileName] = useState("");
    const [uploadCompleted, setUploadCompleted] = useState(false);
    const [uploadedFileId, setUploadedFileId] = useState(null);
    const [uploadLimitError, setUploadLimitError] = useState(false);
    const [selectedFileDurationSeconds, setSelectedFileDurationSeconds] = useState(0);
    const [transcriptionEstimateState, setTranscriptionEstimateState] = useState({
        open: false,
        fileId: null,
        audioDurationSeconds: 0
    });
    const packageModalCtx = usePackageModal();
    const userCtx = useUser();
    const hiddenContentSwitchRef = useRef(null);
    const fileInputRef = useRef(null);
    const autoOpenTriggeredRef = useRef(false);
    const preselectedFileHandledRef = useRef(false);

    useEffect(() => {
        if (open) {
            if (userCtx.hasReachLimit()) return setUploadLimitError(true);
            resetState();
            autoOpenTriggeredRef.current = false;
            preselectedFileHandledRef.current = false;
        }
    }, [open]);

    useEffect(() => {
        if (!open || !autoOpenFilePicker || uploadLimitError || loading || uploadCompleted || autoOpenTriggeredRef.current) {
            return;
        }

        autoOpenTriggeredRef.current = true;
        window.setTimeout(() => {
            fileInputRef.current?.click();
        }, 0);
    }, [autoOpenFilePicker, loading, open, uploadCompleted, uploadLimitError]);

    useEffect(() => {
        if (!open || !preselectedFile || loading || preselectedFileHandledRef.current) {
            return;
        }

        preselectedFileHandledRef.current = true;
        uploadSelectedFile(preselectedFile);
    }, [loading, open, preselectedFile]);

    const resetState = () => {
        setUploadCompleted(false);
        setUploadedFileId(null);
        setUploadLimitError(false);
        setSelectedFileDurationSeconds(0);
    };

    const uploadSelectedFile = async (selectedFile) => {
        if (!selectedFile) return;

        setUploadLimitError(false);
        setFileName(selectedFile.name);
        setUploadCompleted(false);
        setUploadedFileId(null);

        try {
            const durationSeconds = await getAudioDuration(selectedFile);
            setSelectedFileDurationSeconds(durationSeconds);
            if (!userCtx.canUploadSeconds(durationSeconds)) {
                setUploadLimitError(true);
                return;
            }
        } catch (error) {
            console.warn('Unable to validate audio duration before upload', error);
        }

        makeRequest('/file', 'post', { audioFile: selectedFile });
    };

    const uploadBtnClickHandler = async (ev) => {
        const selectedFile = ev.target.files?.[0];
        await uploadSelectedFile(selectedFile);
        ev.target.value = null;
    };

    useEffect(() => {
        if (response) {
            if (response.state === 'success') {
                if (response.data && response.data.fileId) {
                    setUploadedFileId(response.data.fileId);
                    setUploadCompleted(true);
                }
            } else {
                // TODO: gestire errore
            }
        }
        if (requestError) {
            if (requestError.error?.code === 400) {
                setUploadLimitError(true);
            }
        }
    }, [response, requestError]);

    useEffect(() => {
    }, [uploadProgress]);

    const cancelBtnClickHandler = async () => {
        if (uploadedFileId) {
            await makeRequest('/file/delete', 'post', { fileId: uploadedFileId });
        } else {
            console.warn('Cancel button clicked but no fileId available');
        }
        setUploadCompleted(false);
        setUploadedFileId(null);
        await onFileUpload();
        onClose();
    };

    const transcribeBtnClickHandler = async () => {
        if (!uploadedFileId) {
            return;
        }

        const result = await makeRequest('/file/transcribe', 'post', {
            fileId: uploadedFileId,
            folderId: currentPatientId,
            hideSensitiveContent: hiddenContentSwitchRef.current
        });

        if (result?.state !== 'success') {
            return;
        }

        setTranscriptionEstimateState({
            open: true,
            fileId: uploadedFileId,
            audioDurationSeconds: selectedFileDurationSeconds
        });
        setUploadCompleted(false);
        setUploadedFileId(null);
        userCtx.fetchUsage();
        onFileUpload();
        if (onTranscriptionStarted) {
            onTranscriptionStarted();
            return;
        }
        onClose();
    };

    const onBuyCreditsBtnClick = () => {
        onClose();
        packageModalCtx.openModal();
    };

    return <>
        <TranscriptionEstimateDialog
            open={transcriptionEstimateState.open}
            fileId={transcriptionEstimateState.fileId}
            audioDurationSeconds={transcriptionEstimateState.audioDurationSeconds}
            onConfirmError={() => {
                setUploadCompleted(false);
                setUploadedFileId(null);
                setUploadLimitError(false);
                setSelectedFileDurationSeconds(0);
                if (onRetryAfterError) {
                    onRetryAfterError();
                }
            }}
            onClose={() => {
                setTranscriptionEstimateState({
                    open: false,
                    fileId: null,
                    audioDurationSeconds: 0
                });
            }}
        />
        <Dialog
            open={open}
            onClose={onClose}
        >
            <Stack direction="row">
                <DialogTitle sx={{ flexGrow: 1 }}>
                    {uploadLimitError ? "" : uploadCompleted ? "File caricato al 100%" : "Nuovo file"}
                </DialogTitle>
                <Button variant="text" color="error" size="small" onClick={onClose}><ClearIcon /></Button>
            </Stack>
            <DialogContent>
                {!uploadLimitError && !loading && !uploadCompleted && <>
                    <DialogContentText>
                        Aggiungi un nuovo file da trascrivere.
                        <br />Il processo di trascrizione iniziera una volta caricato il file.
                    </DialogContentText>
                    {currentPatientId && (
                        <Typography mt={2} textAlign="center" variant="body2" sx={{ opacity: 0.65 }}>
                            Il file verra caricato per il paziente <strong>{currentPatientName}</strong>.
                        </Typography>
                    )}
                    <Stack mt={4} alignItems="center" justifyContent="center">
                        <Button
                            size={"large"}
                            component={'label'}
                            tabIndex={-1}
                            variant="contained"
                            color="primary"
                            startIcon={<FileUploadIcon />}
                        >
                            Seleziona file
                            <VisuallyHiddenInput
                                ref={fileInputRef}
                                type="file"
                                accept="audio/mp3,audio/*"
                                onChange={uploadBtnClickHandler}
                            />
                        </Button>
                    </Stack>
                </>}
                {(!uploadLimitError && (loading || uploadCompleted)) && <Stack alignItems="center" justifyContent="center">
                    {!uploadCompleted && (
                        <Typography variant="body1" mb={2} component="div">
                            Caricamento file in corso...
                        </Typography>
                    )}
                    <UploadProgress uploadPercent={uploadProgress} />
                    <Typography pt={1} variant="body1">{fileName}</Typography>
                    {uploadCompleted && (
                        <HiddenContentSwitch ref={hiddenContentSwitchRef} />
                    )}
                    <Stack direction="row" spacing={2} mt={2}>
                        <Button variant="outlined" onClick={uploadCompleted ? cancelBtnClickHandler : cancelRequest} startIcon={<ClearIcon />} sx={cancelButtonSx}>
                            Annulla
                        </Button>
                        {uploadCompleted && (
                            <Button variant="contained" onClick={transcribeBtnClickHandler}>
                                Trascrivi
                            </Button>
                        )}
                    </Stack>
                </Stack>}
                {(uploadLimitError) && <Stack alignItems='center' justifyContent="center">
                    <ErrorIcon sx={{ fontSize: '4rem' }} color={'error'} />
                    <Typography mt={2} color={"error"} variant='h4'>Minuti insufficienti</Typography>
                    <Typography textAlign='center' variant='h6'>Non hai abbastanza crediti per completare la trascrizione di questo audio</Typography>
                    <Button sx={{ mt: 4 }} variant="contained" size='large' onClick={onBuyCreditsBtnClick}>Acquista crediti</Button>
                </Stack>}
            </DialogContent>
        </Dialog>
    </>;
};

export default FileUploadDialog;
